import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import supabaseClient from '@/api/supabaseClient';

export interface Tip {
  title: string;
  content: string;
}

export interface QuestionCategory {
  id: string;
  category_code: string;
  category: string;
  name: string;
  description: string | null;
  subject: string;
  paper_number: string;
  content: string | null;
  tips: Tip[] | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateQuestionCategoryParams {
  id: string;
  name?: string;
  description?: string | null;
  content?: string | null;
  tips?: Tip[] | null;
}

const fetchQuestionCategories = async (): Promise<QuestionCategory[]> => {
  const { data, error } = await supabaseClient
    .from('question_categories')
    .select('*')
    .order('category_code', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return data as QuestionCategory[];
};

const fetchQuestionCategory = async (id: string): Promise<QuestionCategory> => {
  const { data, error } = await supabaseClient
    .from('question_categories')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data as QuestionCategory;
};

const updateQuestionCategory = async (params: UpdateQuestionCategoryParams): Promise<QuestionCategory> => {
  const { id, ...updateData } = params;

  const { data, error } = await supabaseClient
    .from('question_categories')
    .update({ ...updateData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data as QuestionCategory;
};

export const useAdminQuestionCategories = () => {
  return useQuery({
    queryKey: ['admin-question-categories'],
    queryFn: fetchQuestionCategories,
  });
};

export const useAdminQuestionCategory = (id: string | undefined) => {
  return useQuery({
    queryKey: ['admin-question-category', id],
    queryFn: () => fetchQuestionCategory(id!),
    enabled: !!id,
  });
};

export const useUpdateQuestionCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateQuestionCategory,
    onSuccess: (data) => {
      toast.success('Category updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-question-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-question-category', data.id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update category');
    },
  });
};

export default useAdminQuestionCategories;
